/**
 * Weather condition computation based on meteorological standards
 * Matches the original script.js computeWeatherCondition function
 */

export type WeatherCondition =
  | 'sunny'
  | 'cloudy'
  | 'partly-cloudy'
  | 'rainy'
  | 'foggy'
  | 'hot'
  | 'cold'
  | 'unknown';

export interface WeatherData {
  temperature: number | null;
  humidity: number | null;
  pressure?: number | null;
  light?: number | null;
  rain?: number | null;
  windSpeed?: number | null;
}

export const computeWeatherCondition = (
  temp: number | null,
  hum: number | null,
  press: number | null = null,
  light: number | null = null,
  rain: number | null = null,
  windSpeed: number | null = null
): WeatherCondition => {
  if (temp === null || hum === null) {
    return 'unknown';
  }
  const wind = typeof windSpeed === 'number' ? windSpeed : null;

  // Priority order: keep conservative thresholds to avoid false rain

  // Rain requires sensor >= 50%; ignore mere high humidity or low pressure alone
  if (rain !== null && rain >= 50) {
    return 'rainy';
  }

  // Foggy: low light + very high humidity
  if (light !== null && hum > 90 && light < 20) {
    return 'foggy';
  }

  // Stormy / unstable: very low pressure
  if (press !== null && press < 980) {
    return 'cloudy';
  }

  // Windy: near-gale or higher
  if (wind !== null && wind > 15) {
    return 'cloudy';
  }

  // Hot / cold
  if (temp < 15) return 'cold';
  if (temp > 35) return 'hot';
  if (temp > 30 && hum < 40) return 'hot';

  // Sunny / partly / cloudy primarily by light + humidity
  if (light !== null) {
    if (light > 80 && hum < 60) return 'sunny';
    if (light >= 40 && light <= 80) return 'partly-cloudy';
    if (light < 40) return 'cloudy';
  }

  // Humidity-only fallback
  if (hum < 50 && temp > 15) return 'sunny';
  if (hum >= 50 && hum < 65) return 'partly-cloudy';
  if (hum >= 65) return 'cloudy';

  return 'unknown';
};

export const getWeatherConditionLabel = (condition: WeatherCondition): string => {
  const labels: Record<WeatherCondition, string> = {
    sunny: 'Sunny',
    cloudy: 'Cloudy',
    'partly-cloudy': 'Partly Cloudy',
    rainy: 'Rainy',
    foggy: 'Foggy',
    hot: 'Hot & Dry',
    cold: 'Cold',
    unknown: 'Unknown',
  };
  return labels[condition] || 'Unknown';
};

